import { mutateStockPlans } from "@/api/stockPlan/useStockPlanQuery";
import { useModal } from "@/features/modal/ModalStackManager";
import { FormButtonBox } from "@/features/standard/form/FormButtonBox";
import { FormMain } from "@/features/standard/form/FormMain";
import { FormWrapper } from "@/features/standard/form/FormWrapper";
import customAlert from "@/features/ui/alert/Alert";
import { handleErrorResponse } from "@/utils/errorMessage";
import { Button, Select } from "@mantine/core";
import { useForm } from "@mantine/form";
import {
  StockPlansGet200ResponseRowsInner,
  StockPlansGet200ResponseRowsInnerStatusEnum,
  WmsApiStockPlansStockPlanIdPutRequest,
} from "@sizlcorp/sizl-api-document/dist/models";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
interface StockPlanStatusFormProps {
  formatterProps: StockPlansGet200ResponseRowsInner | undefined;
}

const Update = Object.assign(FormMain, {
  Button: Button,
  Select: Select,
  Wrapper: FormWrapper,
  ButtonBox: FormButtonBox,
  Text: Text,
});

export const StockPlanStatusForm = (params: StockPlanStatusFormProps) => {
  const { formatterProps } = params;
  const { closeModal } = useModal();
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  const { mutate: updateMutate } = useMutation(
    (params: WmsApiStockPlansStockPlanIdPutRequest) =>
      mutateStockPlans
        .update(params)
        .mutationFn(params as WmsApiStockPlansStockPlanIdPutRequest | any),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["stockPlans"]);
        closeModal(form.values);
        customAlert(
          t("message.editSuccess", { item: t("stockPlan.stockPlan") }),
          t("common.editSuccess"),
          "green",
        );
      },
      onError: (error: any) => {
        customAlert(
          handleErrorResponse({ error, errorContext: t("stockPlan.stockPlan") }),
          t("common.editFail", { item: t("stockPlan.stockPlan") }),
          "red",
        );
      },
    },
  );

  const form = useForm({
    initialValues: {
      scheduledAt: formatterProps?.scheduledAt ? new Date(formatterProps?.scheduledAt) : undefined,
      itemCode: formatterProps?.itemCode,
      direction: formatterProps?.direction,
      quantity: formatterProps?.quantity,
      status: formatterProps?.status,
    },
  });

  const onSubmit = async () => {
    try {
      updateMutate({
        stockPlanId: formatterProps?.id!,
        stockPlansGetRequest: {
          scheduledAt: form.values.scheduledAt?.toString()!,
          itemCode: form.values.itemCode!,
          direction: formatterProps?.direction!,
          quantity: form.values.quantity!,
          status: form.values.status!,
        },
      });
    } catch (error) {
      customAlert(
        handleErrorResponse({ error, errorContext: t("stockPlan.stockPlan") }),
        t("common.editFail", { item: t("stockPlan.stockPlan") }),
        "red",
      );
    }
  };

  return (
    <Update.Wrapper>
      <Select
        label={t("stockPlan.status")}
        dropdownPosition="bottom"
        placeholder={t("placeholder.select", { item: t("stockPlan.status") })}
        data={[
          {
            label: t("stockPlanStatus.PLANNED"),
            value: StockPlansGet200ResponseRowsInnerStatusEnum.PLANNED,
          },
          {
            label: t("stockPlanStatus.DONE"),
            value: StockPlansGet200ResponseRowsInnerStatusEnum.DONE,
          },
          {
            label: t("stockPlanStatus.CANCELED"),
            value: StockPlansGet200ResponseRowsInnerStatusEnum.CANCELED,
          },
        ]}
        withAsterisk
        {...form.getInputProps("status")}
      />
      <Update.ButtonBox>
        <Update.Button color="gray" onClick={closeModal}>
          {t("common.cancel")}
        </Update.Button>
        <Update.Button onClick={onSubmit}>
          {t("common.edit", { item: t("stockPlan.stockPlan") })}
        </Update.Button>
      </Update.ButtonBox>
    </Update.Wrapper>
  );
};
